import { useForm, useFieldArray, FormProvider } from 'react-hook-form'
import { Input } from 'src/components/inputs/Input'
import { CoachCategory } from 'src/gql/graphql'
import { useTranslation } from 'react-i18next'
import { useContext, useMemo, useState } from 'react'
import { FormValues, PathwayFormType } from './types'
import PathwayProgramInput from './PathwayProgramInput'
import { DatePickerInput } from '../inputs/DatePickerInput'
import PageHeader from '../common/PageHeader'
import { AuthContext } from '../Auth/AuthProvider'
import cloneDeep from 'lodash/cloneDeep'
import { toast } from 'react-toastify'
import { useHistory } from 'react-router-dom'
import dayjs from 'dayjs'

const transformPathway = (pathWayData: PathwayFormType) => {
  if (!pathWayData || !Array.isArray(pathWayData.programs)) {
    throw new Error('Invalid pathway data')
  }

  const cloned = cloneDeep(pathWayData)
  let currentDate: Date
  try {
    currentDate = cloned.due_date ? new Date(cloned.due_date) : new Date()
    if (isNaN(currentDate.getTime())) throw new Error('Invalid due_date')
  } catch (e) {
    currentDate = new Date()
  }

  cloned.programs.forEach((program) => {
    if (!Array.isArray(program.tasks)) return
    program.tasks.forEach((task) => {
      const endOfDay = dayjs(currentDate).endOf('day')
      task.due_date = endOfDay.toISOString()
      task.frequency_repeats = Number(task.frequency_repeats)
      currentDate.setDate(currentDate.getDate() + 1)
    })
  })

  delete cloned.due_date
  return cloned
}

export default function PathwayForm() {
  const { t } = useTranslation()
  const { userInfo } = useContext(AuthContext)
  const [isLoading, setIsLoading] = useState(false)
  const userId = userInfo?.stsUserId
  const orgId = userInfo?.organization?.stsOrganisationId
  const history = useHistory()
  const form = useForm<FormValues>({
    defaultValues: {
      pathways: [
        {
          title: '',
          description: '',
          programs: [
            {
              title: '',
              description: '',
              tasks: [
                {
                  title: '',
                  description: '',
                  coach_category: '',
                  due_date: '',
                  task_type: '',
                  frequency_unit: 'once',
                  frequency_repeats: 1,
                },
              ],
            },
          ],
        },
      ],
    },
  })

  const coachCategoryOptions = useMemo(
    () => [
      {
        key: 'psychology',
        value: CoachCategory.Psychology,
        label: t('common.word.psychology', 'Psychology'),
      },
      {
        key: 'business',
        value: CoachCategory.Business,
        label: t('common.word.business', 'Business'),
      },
    ],
    [t],
  )

  const taskFrequencyOptions = useMemo(
    () => [
      {
        key: 'once',
        value: 'once',
        label: t('common.word.once', 'Once'),
      },
      {
        key: 'daily',
        value: 'daily',
        label: t('common.word.onceADay', 'Once a day'),
      },
      {
        key: 'once_weekly',
        value: 'once_weekly',
        label: t('common.word.onceAWeek', 'Once a week'),
      },
      {
        key: 'twice_daily',
        value: 'twice_daily',
        label: t('common.word.twiceAWeek', 'Twice a week'),
      },
    ],
    [t],
  )

  const taskCategoryOptions = useMemo(
    () => [
      {
        key: 'caiSession',
        value: 'CAI_SESSION',
        label: t('common.word.CaiSession', 'Cai Session'),
      },
      {
        key: 'irl',
        value: 'CAI_IRL',
        label: t('common.word.IRL', 'IRL'),
      },
    ],
    [t],
  )

  const { control } = form

  const pathwaysFieldArray = useFieldArray({
    control,
    name: 'pathways',
  })

  const onSubmit = async (data: FormValues) => {
    const transformedData = transformPathway(data.pathways[0])
    const result = {
      user_id: userId,
      organisation_id: orgId,
      title: transformedData.title,
      description: transformedData.description,
      pathway: transformedData,
    }

    try {
      setIsLoading(true)
      const resp = await fetch(
        `${process.env.REACT_APP_CARCH_ENDPOINT}/goals/nested`,
        {
          method: 'POST',

          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
          body: JSON.stringify(result),
        },
      )
      if (!resp.ok) {
        toast.error(t('common.toast.error'))
        return
      }
      toast.success(t('common.toast.createGoalSuccess'))
      history.push('/goals')
    } catch (error) {
      toast.error(t('common.toast.error'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="container-xxl">
      <PageHeader
        showBackButton
        headerTitle={t('common.button.createPathway')}
      />
      <FormProvider {...form}>
        <div className="d-flex flex-column gap-3" style={{}}>
          <div
            className="card"
            style={{
              background: '#f4f6fb',
              border: 'none',
              boxShadow: '0 2px 12px 0 rgba(80, 80, 120, 0.07)',
              borderRadius: 18,
            }}
          >
            <div className="card-body">
              <form onSubmit={form.handleSubmit(onSubmit)}>
                {pathwaysFieldArray.fields.map((pathway, pIdx) => (
                  <div key={pathway.id}>
                    <div className="row mb-3">
                      <div className="col-6">
                        <Input
                          required
                          label={t('resource.pathway.field.pathwayTitle')}
                          source={`pathways.${pIdx}.title`}
                        />
                      </div>
                      <div className="col-6">
                        <Input
                          label={t('resource.pathway.field.pathwayDescription')}
                          source={`pathways.${pIdx}.description`}
                        />
                      </div>
                      <div className="col-12 mt-2">
                        <DatePickerInput
                          required
                          format="MM/DD/YYYY"
                          label={t('common.word.startDate', 'Start date')}
                          source={`pathways.${pIdx}.due_date`}
                        />
                      </div>
                    </div>
                    <PathwayProgramInput
                      coachCategoryOptions={coachCategoryOptions}
                      control={control}
                      pIdx={pIdx}
                      taskCategoryOptions={taskCategoryOptions}
                      taskFrequencyOptions={taskFrequencyOptions}
                    />
                  </div>
                ))}
                <div className="d-flex justify-content-end mt-3">
                  <button
                    className="btn btn-primary"
                    disabled={isLoading}
                    type="submit"
                  >
                    {t('common.button.save')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </FormProvider>
    </div>
  )
}
